import * as vp from 'vopay';
import { FunctionComponent, useRef, useEffect } from 'react';
import 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import type { Chart, TooltipItem, ChartType } from 'chart.js';

const { getCSSVariable } = vp;

export type TransactionChartProps = {
  options: {
    label: string;
    fill: boolean;
    tension: number;
    tickSpace?: number;
  };
  data: {
    label: string;
    value: string | number;
  }[];
};

type FC = FunctionComponent<TransactionChartProps>;

const TransactionChart: FC = ({ data, options }) => {
  const chartRef = useRef<Chart<'line', (string | number)[], unknown> | null>(null);
  const tickSpace = options.tickSpace;

  const getPrimaryColor = () => getCSSVariable('--primaryColor') || '#000';

  const plugin = {
    id: 'customGradientFill',
    afterLayout(chart: Chart) {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;

      const color = getPrimaryColor() || '#000';

      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      gradient.addColorStop(0, `${color}33`); // 20% opacity
      gradient.addColorStop(1, `${color}00`); // 0% opacity
      chart.data.datasets[0].backgroundColor = gradient;
    },
  };

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update();
    }
  }, []);

  return (
    <div style={{ height: '200px', width: '100%' }}>
      <Line
        ref={chartRef}
        data={{
          labels: data.map((item) => item.label),
          datasets: [
            {
              label: options.label,
              fill: options.fill,
              tension: options.tension,
              data: data.map((item) => item.value),
              borderColor: getPrimaryColor(),
              pointRadius: 0,
              pointHoverRadius: 4,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'nearest',
            intersect: false,
          },
          scales: {
            x: {
              ticks: {
                color: 'rgba(45, 45, 45, 0.5)',
                autoSkip: false, // prevents removing the label when tilted
                align: 'center',
                maxRotation: 0, // prevents tilting the label
                minRotation: 0, // prevents tilting the label
                ...(tickSpace && {
                  callback: function (_value, index: number) {
                    return index % tickSpace === 0 ? this.getLabelForValue(index) : '';
                  },
                }),
              },
              grid: { display: false },
            },
            y: {
              ticks: { maxTicksLimit: 4, color: 'rgba(45, 45, 45, 0.50)' },
              grid: { display: false },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: (context: TooltipItem<ChartType>[]) => context[0].label || '',
                label: () => '',
                footer: (context: TooltipItem<ChartType>[]) => {
                  const value = context[0].dataset.data[context[0].dataIndex]?.toString() || '';
                  return `Transactions: ${value}`;
                },
              },
              padding: { left: 32, right: 32, top: 16, bottom: 16 },
              backgroundColor: '#2D2D2D',
              titleColor: '#FBFBFB',
              titleFont: { size: 16, weight: 600 },
              titleAlign: 'center',
              footerColor: 'rgba(251, 251, 251, 0.75)',
              footerFont: { size: 16, weight: 400 },
              footerAlign: 'center',
              footerMarginTop: -2,
              bodyAlign: 'center',
              bodyColor: 'rgba(251, 251, 251, 0.75)',
              displayColors: false,
              caretSize: 6,
              borderColor: 'none',
              cornerRadius: 12,
            },
          },
        }}
        plugins={[plugin]}
      />
    </div>
  );
};

export default TransactionChart;
